import { RadioGroup } from "@package/subframe/components/RadioGroup";
import { TextArea } from "@package/subframe/components/TextArea";
import { TextField } from "@package/subframe/components/TextField";

import { INCOMPLETE_DATE } from "@/lib/people-profile-sections";

/** Radix radio items can't hold an empty value, so "not recorded" needs a stand-in. */
const NONE = "__none__";

interface SectionTextFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  error?: string;
  helpText?: string;
  type?: "text" | "tel" | "email";
}

export function SectionTextField({
  label,
  name,
  value,
  onChange,
  disabled,
  error,
  helpText,
  type = "text",
}: SectionTextFieldProps) {
  return (
    <TextField
      className="h-auto w-full"
      size="large"
      label={label}
      error={!!error}
      helpText={error ?? helpText}
    >
      <TextField.Input
        type={type}
        name={name}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      />
    </TextField>
  );
}

type SectionDateFieldProps = Omit<SectionTextFieldProps, "type" | "helpText">;

/**
 * A part-typed date reads as "" from the browser; it is reported as
 * INCOMPLETE_DATE so it fails validation instead of clearing the date. The
 * input is still given "" then, which matches what the browser already holds,
 * so React leaves the half-typed text alone.
 */
export function SectionDateField({
  label,
  name,
  value,
  onChange,
  disabled,
  error,
}: SectionDateFieldProps) {
  return (
    <TextField
      className="h-auto w-full"
      size="large"
      label={label}
      error={!!error}
      helpText={error}
    >
      <TextField.Input
        type="date"
        name={name}
        value={value === INCOMPLETE_DATE ? "" : value}
        max="9999-12-31"
        onChange={(event) =>
          onChange(
            event.target.validity.badInput
              ? INCOMPLETE_DATE
              : event.target.value,
          )
        }
        disabled={disabled}
      />
    </TextField>
  );
}

interface SectionTextAreaProps {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  error?: string;
}

export function SectionTextArea({
  label,
  name,
  value,
  onChange,
  disabled,
  error,
}: SectionTextAreaProps) {
  return (
    <TextArea
      className="h-auto w-full"
      label={label}
      error={!!error}
      helpText={error}
    >
      <TextArea.Input
        name={name}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      />
    </TextArea>
  );
}

interface SectionRadioGroupProps<V extends string> {
  label: string;
  value: V | "";
  options: { value: V; label: string }[];
  onChange: (value: V | "") => void;
  disabled: boolean;
  error?: string;
}

/**
 * Radios, not a Select, for the sections' short choices: a Select's list opens
 * in a portal outside the card's session-recording mask.
 */
export function SectionRadioGroup<V extends string>({
  label,
  value,
  options,
  onChange,
  disabled,
  error,
}: SectionRadioGroupProps<V>) {
  const selected = value || NONE;

  return (
    <RadioGroup
      label={label}
      aria-label={label}
      value={selected}
      onValueChange={(next) => onChange(next === NONE ? "" : (next as V))}
      disabled={disabled}
      error={!!error}
      helpText={error}
    >
      {[...options, { value: NONE, label: "Not recorded" }].map((option) => (
        // Subframe's option passes `checked` through and defaults it to
        // false, which would override the group's own value.
        <RadioGroup.Option
          key={option.value}
          value={option.value}
          label={option.label}
          checked={selected === option.value}
          disabled={disabled}
        />
      ))}
    </RadioGroup>
  );
}

export const YES_NO_OPTIONS: { value: "yes" | "no"; label: string }[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];
